import { FC, useEffect, useState } from "react";
import WorkInfoCard from "./WorkInfoCard";
import CreateApplication from "./application/CreateApplication";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    isExpressWork,
    selectCurrentUserApplication,
    selectWorkAvailableToAddApplication,
    selectWorkById,
} from "../../../slices/works-slice";
import { WorkServiceImpl } from "../../../services/WorkServiceImpl";
import { useErrorHandler } from "../../../custom-hooks/base-hooks/useErrorHandler";
import { StatusCodes } from "http-status-codes";
import ApplicationSubmissionSuccess from "./application/ApplicationSubmissionSuccess";
import ApplicationsList from "./application/ApplicationsList";
import UserContactsCard from "../../resources/components/UserContactsCard";
import { useTranslation } from "react-i18next";
import { selectUserCompletion } from "../../../slices/user-slice";
import { ApplicationServiceImpl } from "../../../services/ApplicationServiceImpl";
import { RECORDS_PER_PAGE } from "../../../domain/constants";

const WorkPage: FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const work = useSelector(selectWorkById(id));
    const isWorkAvailableToAddApplication = useSelector(selectWorkAvailableToAddApplication(work));
    const currentApplication = useSelector(selectCurrentUserApplication(work));
    const isUserAccountComplete = useSelector(selectUserCompletion());
    const [applicationSuccessMessageKey, setApplicationSuccessMessageKey] = useState("");
    const [applicationsPage, setApplicationsPage] = useState(0);
    const [workIsLoaded, setWorkIsLoaded] = useState(false);
    const showSubmissionSuccess = !!currentApplication || !!applicationSuccessMessageKey;
    const isExpress = isExpressWork(work);
    const { isError: notFound, resetErrors: resetNotFoundErrors } = useErrorHandler(StatusCodes.NOT_FOUND);
    const { isError: invalidIdError, resetErrors: resetBadRequestErrors } = useErrorHandler(StatusCodes.BAD_REQUEST);

    useEffect(() => {
        let active = true;
        const uploadData = async () => {
            const result = await dispatch(WorkServiceImpl.getWorkByIdThunk({ id }));
            if (active) setWorkIsLoaded(result.meta.requestStatus === "fulfilled");
        };
        uploadData();

        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") uploadData();
        };
        document.addEventListener("visibilitychange", refreshWhenVisible);
        window.addEventListener("focus", uploadData);
        return () => {
            active = false;
            document.removeEventListener("visibilitychange", refreshWhenVisible);
            window.removeEventListener("focus", uploadData);
        };
    }, [dispatch, id]);

    useEffect(() => {
        return () => {
            resetNotFoundErrors();
            resetBadRequestErrors();
        };
    }, []);

    const deleteApplicationCallback = () => {
        setApplicationSuccessMessageKey("work.applicationCreation.deletionSuccess");
    };

    const successApplicationCreation = (messageKey: string) => {
        setApplicationSuccessMessageKey(messageKey);
    };

    const uploadApplicationsCallback = async () => {
        setApplicationsPage((prev) => prev + 1);
        await dispatch(
            ApplicationServiceImpl.getWorkApplicationsThunk({
                params: { page: applicationsPage + 1, limit: RECORDS_PER_PAGE, offset: 0 },
                workId: work.id,
            })
        );
    };

    if (notFound() || invalidIdError()) {
        navigate("/not-found", { replace: true });
        return;
    }
    if (!work || !workIsLoaded) {
        return <></>;
    }

    return (
        <div className="relative mx-auto dmin2:w-[1280px] di6:w-[800px] ti6:w-[650px] ti7:w-[560px] tmax4:w-[360px]">
            <WorkInfoCard work={work} />
            <div className="flex tmax5:flex-col tmin5:justify-between tmin5:mb-[60px]">
                <div className="flex flex-col items-start dmin2:max-w-[626px] di6:max-w-[439px] ti6:max-w-[289px]">
                    <h2 className="H24B dmax2:H20B text-textWhite mb-4">{t("work.jobDescription")}</h2>
                    <p className="H16 dmax2:H14 text-textFaded max-w-full break-words td3:pr-5">{work.description}</p>
                </div>
                <UserContactsCard work={work} />
            </div>
            {isWorkAvailableToAddApplication && !isExpress && isUserAccountComplete && (
                <CreateApplication work={work} successCallback={successApplicationCreation} />
            )}
            {showSubmissionSuccess && (
                <ApplicationSubmissionSuccess
                    messageKey={
                        applicationSuccessMessageKey ||
                        (currentApplication ? "work.applicationCreation.creationSuccess" : "")
                    }
                />
            )}
            <ApplicationsList
                work={work}
                uploadApplicationsCallback={uploadApplicationsCallback}
                deleteApplicationCallback={deleteApplicationCallback}
            />
        </div>
    );
};

export default WorkPage;
